/**
 * CALGARY-SE — roster wave entries. GENERATED BY ASSEMBLY, WRITTEN BY REVIEW.
 *
 * Geography (streets, landmarks, coordinates, adjacency) ported from the source
 * roster; airspace derived by the calibrated classifier in
 * scripts/derive-airspace.mjs; every descriptive sentence written fresh from
 * aerial-media truth and fact-checked against its generation brief. See
 * docs/geo/VISION-v3.md for the fact contract these entries were written under.
 *
 * 28 entries. Edit by hand freely — assembly never overwrites a
 * file that has drifted from its fragments without --force.
 */
import type { Location } from "../types";

export const ROSTER_CALGARY_SE: Location[] = [
// ──────────────────────────────────────────────────────────────── ACADIA ──
  {
    slug: "acadia",
    name: "Acadia",
    region: "calgary-se",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.9722, lng: -114.0794 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Kirkby Field (Indus)",
      distanceToAerodromeKm: 16,
      authorizationRequired: false,
      authorizationNote:
        "Acadia sits in uncontrolled Class G airspace, clear of the charted Calgary-area control zones and about 16 km from Kirkby Field (Indus). Authorisation is not normally required here. Flight plans here also keep clear of Rockyview General Hospital heliport, operating about 2 km away. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Rockyview General Hospital heliport"],
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "From altitude Acadia reads as an established grid: long, low rooflines on wide lots running in straight lines off Acadia Drive SE and Acadia Boulevard SE, with mature canopy closing over the side streets between them.",
      consequence:
        "None of that survives a ground-level photograph — from the street, an Acadia bungalow reads like any bungalow, and the lot width, canopy and spacing that set the community apart are exactly what the camera cannot hold.",
      resolution:
        "A low-altitude orbit above the property line puts the full lot, the canopy and the neighbouring spacing into one frame, flown from a private-property launch and delivered in the standard turnaround.",
    },
    landmarks: [
      "Acadia Drive SE",
      "Acadia Boulevard SE",
      "Acadia Recreation Complex",
      "Southland Leisure Centre",
      "Acadia Athletic Park",
    ],
    terrain:
      "Flat, established tableland in the inner southeast — elevation change is measured in single metres, so the aerial frame is about lot pattern and canopy rather than relief.",
    windNote:
      "Chinook belt, with open tableland offering no shelter — a west-wind day is a gusty day at altitude, so the flight window is chosen against gust spread rather than the daily average.",
    seasonalWindow:
      "The mature canopy sets the look: full leaf flatters residential frames through summer, while the bare months open sightlines to rooflines and lot pattern. Golden hour swings from late afternoon in winter to mid-evening in June.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone takeoff and landing in municipal parks and on pathways, so Acadia launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "About 20 minutes from the base. Same-day scheduling is realistic, and an Acadia booking pairs naturally with Willow Park, Lake Bonavista or Fairview in a single trip.",
    faqs: [
      {
        question: "Do I need airspace approval for a drone shoot in Acadia?",
        answer:
          "Normally, no. Acadia lies in uncontrolled Class G airspace outside the charted Calgary-area control zones, about 16 km from Kirkby Field at Indus. The Rockyview General Hospital heliport operates about 2 km away, so flight plans keep clear of its approaches, and the specific site and altitude are confirmed against NAV Drone before every flight.",
      },
      {
        question: "What does aerial photography add for an established community like Acadia?",
        answer:
          "Lot width and canopy. Acadia's value sits in things a ground camera cannot hold in one frame — the spacing between homes, the mature trees, and how close a property sits to the Acadia Recreation Complex or Southland Leisure Centre. A single elevated frame carries all three.",
      },
    ],
    primaryKeywords: [
      "drone photography Acadia",
      "aerial photography Acadia",
      "Acadia drone services",
    ],
    nearestLocations: ["willow-park", "lake-bonavista", "fairview"],
  },

  // ─────────────────────────────────────────────────────────── ALBERT PARK ──
  {
    slug: "albert-park",
    name: "Albert Park",
    region: "calgary-se",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0289, lng: -113.9997 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 10,
      authorizationRequired: true,
      authorizationNote:
        "Albert Park is about 10 km from Calgary International, near the CYYC control zone's charted boundary; a single address can fall either side of the line. The default plan treats it as controlled airspace until NAV Drone confirms otherwise, with authorisation filed before booking wherever it is required. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Albert Park holds some of the only true high ground in the inner southeast — the community looks west over the flats, with the Calgary Stampede Grounds in the middle distance and the city skyline behind them.",
      consequence:
        "A ground-level photographer at the top of the rise gets a street scene; the westward drop and the skyline behind it — often the property's chief argument — never make the frame.",
      resolution:
        "A westward elevation hold over the property line stacks the rise, the flats and the skyline into one image, with the address checked against the CYYC boundary in NAV Drone and any required authorisation filed before the date is confirmed, delivered in the standard turnaround.",
    },
    landmarks: [
      "Calgary Stampede Grounds",
      "Elliston Park",
      "Max Bell Centre",
      "17 Street SE",
    ],
    terrain:
      "High ground on the east side of the inner city, falling away westward toward the flats — one of the few southeast frames with genuine relief and a skyline horizon.",
    windNote:
      "Chinook-belt meteorology: west winds arrive in surges through winter, and the exposed high ground takes them without shelter, so calm-window planning matters more here than the forecast average.",
    seasonalWindow:
      "The westward outlook means the community's best light is the last of the day, when low sun rakes across the rise; late-summer wildfire smoke is the main threat to a clean skyline horizon.",
    bylawNote:
      "Drone takeoff and landing in City of Calgary parks and pathway corridors is restricted under municipal bylaws; launches here stage from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "About 15 minutes from the base, so the drive is trivial — the NAV Drone boundary check at booking sets the lead time where authorisation is required. Pairs cleanly with Dover or Forest Lawn shoots on the same trip.",
    faqs: [
      {
        question: "Can a drone legally fly in Albert Park this close to the airport?",
        answer:
          "Yes. Albert Park sits about 10 km from Calgary International, near the charted edge of the CYYC control zone — close enough that which side of the line an address falls on decides the paperwork. Each site is planned as controlled airspace until NAV Drone shows otherwise, with any required authorisation filed before the date is booked.",
      },
      {
        question: "Why is Albert Park suited to aerial photography?",
        answer:
          "Elevation. The community holds high ground that looks west over the inner city — a rare outlook for the southeast. That westward drop, and the skyline behind it, cannot be composed from a driveway. From altitude the rise, the view and the property's place on it read in a single frame.",
      },
      {
        question: "How far ahead should an Albert Park shoot be booked?",
        answer:
          "Far enough to cover the paperwork if the address needs it. The drive from the base is about 15 minutes, so distance is not the constraint — the boundary check in NAV Drone is what decides lead time. Where authorisation is required it is filed at booking, and the date is confirmed once it is in hand.",
      },
    ],
    primaryKeywords: [
      "drone photography Albert Park",
      "aerial photography Albert Park",
      "Albert Park drone services",
    ],
    nearestLocations: ["forest-lawn", "dover", "ramsay", "inglewood"],
  },

  // ──────────────────────────────────────────────────────────── AUBURN BAY ──
  {
    slug: "auburn-bay",
    name: "Auburn Bay",
    region: "calgary-se",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.8975, lng: -113.9522 },
    tier: 3,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Kirkby Field (Indus)",
      distanceToAerodromeKm: 7,
      authorizationRequired: false,
      authorizationNote:
        "Auburn Bay sits in uncontrolled Class G airspace, clear of the charted Calgary-area control zones and about 7 km from Kirkby Field (Indus). Authorisation is not normally required here. South Health Campus heliport operates about 2 km away, and flight plans keep clear of its approaches. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["South Health Campus heliport"],
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Auburn Bay's terrain is almost perfectly flat, so the aerial frame has no relief to work with — Auburn Bay Lake does the compositional work instead, anchoring every wide frame the way a valley or ridge would elsewhere.",
      consequence:
        "For any listing more than a lot or two from the shore, ground photography cannot establish the lake at all — the flat terrain hides it completely, and the buyer never learns how close the water actually is.",
      resolution:
        "A rising reveal from the property brings Auburn Bay Lake up over the rooflines and fixes the walk-to-water distance in a single frame, delivered in the standard turnaround.",
    },
    landmarks: [
      "Auburn Bay Lake",
      "Auburn House",
      "South Health Campus",
      "Auburn Bay Boulevard SE",
    ],
    terrain:
      "Flat southeast prairie graded into crescents around a constructed lake; with no natural relief, the water line and roofline geometry do the compositional work from the air.",
    windNote:
      "On the open southeast edge of the city there is no terrain shelter from chinook-driven west wind, and open-water frames read choppy on gusty days — calm windows are reserved for lake work.",
    seasonalWindow:
      "The lake sets the calendar — open water from ice-off through fall carries the signature frames, and winter freezes it into a flat white field that reads clean from altitude. Long June evenings push golden hour late enough for after-work shoots.",
    bylawNote:
      "City of Calgary bylaws restrict launching or landing in municipal parks and on public pathways, so flights stage from private property with the client's consent — usually the listing itself.",
    subjectMix: ["luxury-residential", "new-build-subdivision", "commercial-retail"],
    mobilisationNote:
      "About 25 minutes from the base. Comfortably same-day when the weather holds, and neighbouring Cranston, Mahogany and Seton bookings can share the same mobilisation.",
    faqs: [
      {
        question: "Does the South Health Campus heliport restrict drone flights in Auburn Bay?",
        answer:
          "It shapes them rather than blocks them. Auburn Bay sits in uncontrolled Class G airspace, about 7 km from Kirkby Field at Indus, so no authorisation is normally required. The hospital heliport operates about 2 km away, and every flight plan keeps clear of its approaches, confirmed against NAV Drone.",
      },
      {
        question: "Can you show the lake for a home that doesn't back onto it?",
        answer:
          "Yes — that is the frame Auburn Bay exists for. The terrain is flat, so from the ground the lake is invisible a street away. From altitude over the property, Auburn Bay Lake sits over the rooflines and the buyer sees exactly how short the walk to the water is.",
      },
      {
        question: "How does an Auburn Bay shoot pair with neighbouring communities?",
        answer:
          "Efficiently. Auburn Bay is about 25 minutes from the base and sits beside Cranston, Mahogany and Seton, so two or three properties across those communities can share one mobilisation. For a single listing, same-day scheduling is realistic whenever wind and light cooperate.",
      },
    ],
    primaryKeywords: [
      "drone photography Auburn Bay",
      "aerial photography Auburn Bay",
      "Auburn Bay drone services",
    ],
    nearestLocations: ["cranston", "mahogany", "seton"],
  },

  // ───────────────────────────────────────────────────────────── CHAPARRAL ──
  {
    slug: "chaparral",
    name: "Chaparral",
    region: "calgary-se",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.8983, lng: -114.0022 },
    tier: 3,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Kirkby Field (Indus)",
      distanceToAerodromeKm: 11,
      authorizationRequired: false,
      authorizationNote:
        "Chaparral lies outside the charted Calgary-area control zones in uncontrolled Class G airspace, about 11 km from Kirkby Field (Indus). No airspace authorisation is normally required. South Health Campus heliport operates about 4 km away, and flight plans keep clear of its approaches. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["South Health Campus heliport"],
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Chaparral carries two wind regimes inside one community: the plateau around Lake Chaparral takes the west wind at full strength, while Chaparral Valley sits low beside Fish Creek Provincial Park where the same wind often arrives softened.",
      consequence:
        "A shoot scheduled off the city forecast alone gets it wrong in both directions — scrubbed on the plateau when the valley was flyable, or flown into rough air that a valley reading would have avoided.",
      resolution:
        "Wind is read for the specific address on the morning of the flight, and the shot list — a lake orbit up top or a valley-floor reveal below — is matched to whichever level the air favours, delivered in the standard turnaround.",
    },
    landmarks: [
      "Lake Chaparral",
      "Chaparral Valley",
      "Fish Creek Provincial Park",
      "Township Shopping",
    ],
    terrain:
      "A two-level community: lake-centred crescents on the plateau, with Chaparral Valley dropping to the low ground along Fish Creek Provincial Park — a rare southeast relief line.",
    windNote:
      "Chinook belt. The plateau is fully exposed to west wind, while the low ground of Chaparral Valley often reads calmer under the same forecast — the two halves can carry different flight windows on the same day.",
    seasonalWindow:
      "Open water on Lake Chaparral from ice-off through fall carries the residential frames; low winter sun rakes the slope between the two levels cleanly, and the valley side holds green along the park edge deep into summer.",
    bylawNote:
      "City of Calgary bylaws restrict park and pathway launches, and Fish Creek Provincial Park carries its own rules on drone operation, so launches stage from private property with the client's consent.",
    subjectMix: ["luxury-residential", "new-build-subdivision"],
    mobilisationNote:
      "About 25 minutes from the base. A straightforward same-day booking, and it pairs well with Walden, Sundance or Legacy on the same run south.",
    faqs: [
      {
        question: "What airspace applies to drone work in Chaparral?",
        answer:
          "Uncontrolled Class G. Chaparral lies outside the charted Calgary-area control zones, about 11 km from Kirkby Field at Indus, so no airspace authorisation is normally required. The South Health Campus heliport operates about 4 km away and flight plans keep clear of its approaches; site and altitude are confirmed in NAV Drone first.",
      },
      {
        question: "Why might Chaparral's flight window differ from the city forecast?",
        answer:
          "Because the community sits at two levels. The plateau around Lake Chaparral takes the west wind at full strength, while Chaparral Valley sits low beside the park and often reads calmer under the same forecast. The wind call is made for the specific address, not for Calgary as a whole.",
      },
      {
        question: "Can Fish Creek Provincial Park appear in the frames?",
        answer:
          "Yes, from aircraft positioned outside it. Provincial park rules restrict drone takeoff and landing on park land, so launches stage from private property; the park's valley and canopy then enter the frame as backdrop. Frames that would require flying from inside the park are removed from the shot list.",
      },
    ],
    primaryKeywords: [
      "drone photography Chaparral",
      "aerial photography Chaparral",
      "Chaparral drone services",
    ],
    nearestLocations: ["walden", "sundance", "legacy"],
  },

  // ─────────────────────────────────────────────────────────── COPPERFIELD ──
  {
    slug: "copperfield",
    name: "Copperfield",
    region: "calgary-se",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.9139, lng: -113.9528 },
    tier: 3,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Kirkby Field (Indus)",
      distanceToAerodromeKm: 7,
      authorizationRequired: false,
      authorizationNote:
        "Copperfield lies outside the charted Calgary-area control zones in uncontrolled Class G airspace, about 7 km from Kirkby Field (Indus). No airspace authorisation is normally required. Flight plans here also keep clear of South Health Campus heliport, operating about 3 km away. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["South Health Campus heliport"],
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Copperfield's airspace profile is rural even though its address is urban: uncontrolled Class G, about 7 km from the small Kirkby Field aerodrome at Indus, and outside every charted Calgary-area control zone.",
      consequence:
        "Clients tend to assume every Calgary address carries airport paperwork and lead time; here that assumption pads the schedule for no reason, and a shoot that could fly this week gets parked into next month.",
      resolution:
        "With no authorisation queue, weather is the only gate — a listing shoot or a construction progress series is booked straight onto the next calm window and delivered in the standard turnaround.",
    },
    landmarks: [
      "South Trail Crossing",
      "Copperfield Park",
      "Big Rock Brewery",
      "Copperfield Boulevard SE",
    ],
    terrain:
      "Flat southeast prairie built out in looping crescents; without relief, aerial frames lean on roofline rhythm, pond edges and the retail geometry at South Trail Crossing.",
    windNote:
      "Squarely in the chinook belt with open prairie exposure; wind is the schedule-mover here, and the gust spread on the morning of the flight decides the window.",
    seasonalWindow:
      "Green-up is the transformation — the same streets read brown in the shoulder seasons and saturated through summer. Winter suits construction progress work, when low sun gives crisp shadows on structure.",
    bylawNote:
      "Municipal parks and pathways are off-limits for drone takeoff and landing under City of Calgary bylaws; Copperfield launches are staged from private property with the client's consent.",
    subjectMix: ["new-build-subdivision", "commercial-retail", "construction-progress"],
    mobilisationNote:
      "About 25 minutes from the base, with no authorisation queue in this airspace — weather is the only gate. Pairs with McKenzie Towne, New Brighton or Cranston in one trip.",
    faqs: [
      {
        question: "Does a Copperfield shoot need Calgary airport authorisation?",
        answer:
          "No. Copperfield sits in uncontrolled Class G airspace outside the charted control zones, about 7 km from Kirkby Field at Indus, so no authorisation is normally required. The South Health Campus heliport operates about 3 km away and flight plans keep clear of its approaches, confirmed against NAV Drone before every flight.",
      },
      {
        question: "How fast can a Copperfield booking turn around?",
        answer:
          "As fast as the weather allows. With no authorisation queue in this airspace and about 25 minutes of driving from the base, the only gate is a calm, clear window. Construction progress series in particular benefit — a missed window is simply reflown on the next suitable morning.",
      },
    ],
    primaryKeywords: [
      "drone photography Copperfield",
      "aerial photography Copperfield",
      "Copperfield drone services",
    ],
    nearestLocations: ["mckenzie-towne", "new-brighton", "cranston"],
  },

  // ────────────────────────────────────────────────────────────── CRANSTON ──
  {
    slug: "cranston",
    name: "Cranston",
    region: "calgary-se",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.8886, lng: -113.9636 },
    tier: 3,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Kirkby Field (Indus)",
      distanceToAerodromeKm: 8,
      authorizationRequired: false,
      authorizationNote:
        "Cranston sits in uncontrolled Class G airspace, clear of the charted Calgary-area control zones and about 8 km from Kirkby Field (Indus). Authorisation is not normally required here. South Health Campus heliport operates about 1 km away, and flight plans keep clear of its approaches. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["South Health Campus heliport"],
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Cranston splits across the Cranston Ridge Escarpment: most of the community sits up on the plateau, while the Riverstone streets — Riverstone Manor SE, Cranbrook Drive SE — sit a full terrace below, on the valley floor beside the water.",
      consequence:
        "Shot from the driveway, a Riverstone address reads like any suburban street — the water beside it and the escarpment above it, the two things the buyer is actually weighing, are both out of frame.",
      resolution:
        "A rising reveal off the valley floor lifts the frame until water, home and escarpment sit in one composition, launched from the property itself and delivered in the standard turnaround.",
    },
    landmarks: [
      "Cranston Ridge Escarpment",
      "Riverstone Manor SE",
      "Cranbrook Drive SE",
      "South Health Campus",
      "Century Hall",
    ],
    terrain:
      "Split terrain: a plateau community above the Cranston Ridge Escarpment, with the Riverstone streets on the valley floor below — the drop reads as one clean diagonal from the air.",
    windNote:
      "Chinook belt, with a local wrinkle: west wind spilling over the Cranston Ridge Escarpment adds turbulence at the rim, so the plateau edge and the valley floor are read as separate wind environments.",
    seasonalWindow:
      "The escarpment face takes the last light of the day, which is the window for ridge-line homes; the valley floor falls into shadow earlier and is scheduled ahead of it. Smoke season is the main late-summer risk.",
    bylawNote:
      "City of Calgary parks bylaws restrict takeoff and landing on municipal park and pathway land, and provincial park rules apply along the valley, so launches stage from private property with consent.",
    subjectMix: ["luxury-residential", "new-build-subdivision", "construction-progress"],
    mobilisationNote:
      "About 25 minutes from the base. Same-day is realistic, and Cranston sits beside McKenzie Lake, Auburn Bay and Seton, so multi-property runs are efficient here.",
    faqs: [
      {
        question: "The South Health Campus heliport is close — can you still fly in Cranston?",
        answer:
          "Yes. Cranston lies in uncontrolled Class G airspace about 8 km from Kirkby Field, so no authorisation is normally required. The heliport operates about 1 km away, which is why every Cranston flight plan is built around its approaches and the specific site and altitude are confirmed in NAV Drone first.",
      },
      {
        question: "Why do Riverstone addresses gain the most from aerial work?",
        answer:
          "Because their two selling points are both out of frame from the ground. A valley-floor listing reads like any suburban street from the driveway; from altitude, the water beside it and the Cranston Ridge Escarpment behind it come back into the picture, which is what the buyer is actually weighing.",
      },
      {
        question: "Does the escarpment change how a Cranston shoot is scheduled?",
        answer:
          "It can. On west-wind days the escarpment rim adds turbulence while the valley floor below stays workable, so the two halves of Cranston are read as separate wind environments. Ridge-line homes also take the last light of the day, which sets the preferred golden-hour slot.",
      },
    ],
    primaryKeywords: [
      "drone photography Cranston",
      "aerial photography Cranston",
      "Cranston drone services",
    ],
    nearestLocations: ["mckenzie-lake", "auburn-bay", "seton"],
  },

  // ──────────────────────────────────────────────────────────── DEER RIDGE ──
  {
    slug: "deer-ridge",
    name: "Deer Ridge",
    region: "calgary-se",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.9573, lng: -114.0447 },
    tier: 3,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Kirkby Field (Indus)",
      distanceToAerodromeKm: 13,
      authorizationRequired: false,
      authorizationNote:
        "In uncontrolled Class G airspace about 13 km from Kirkby Field (Indus), Deer Ridge lies outside the charted Calgary-area control zones, and no authorisation is normally required. Rockyview General Hospital heliport operates about 5 km away, and flight plans keep clear of its approaches. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Rockyview General Hospital heliport"],
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Deer Ridge's street pattern bends around the park rather than through it — Deerpath Road SE and Deercrest Way SE curl along the Fish Creek Provincial Park edge, so from above the community reads as rooflines wrapped around a forest.",
      consequence:
        "The park-backing lots are the premium here, and ground photography reduces that premium to a back fence — the forest behind it, the reason for the price, is invisible.",
      resolution:
        "A boundary-line orbit above the lot puts the valley forest behind the roofline where it belongs, launched from the property so no park land is touched, and delivered in the standard turnaround.",
    },
    landmarks: [
      "Fish Creek Provincial Park",
      "Deerpath Road SE",
      "Deercrest Way SE",
      "Southcentre Mall",
    ],
    terrain:
      "Table-flat on top with the ground falling into the wooded Fish Creek valley along the community's park edge; the line between rooftops and forest is the strongest element in the frame.",
    windNote:
      "Chinook belt. The flat top takes the west wind directly, and the valley edge can add gustiness where the ground drops away, so park-edge frames get the calmer windows.",
    seasonalWindow:
      "Fall is the community's best season from the air, when the valley canopy turns and the park edge lights up behind the rooflines; summer green and clean winter snow both work, but the turn is the premium window.",
    bylawNote:
      "Takeoff and landing inside City of Calgary parks is restricted by municipal bylaw, and Fish Creek Provincial Park has its own drone restrictions, so launches stage from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail"],
    mobilisationNote:
      "About 20 minutes from the base. An easy same-day booking that pairs with Deer Run next door, or with Willow Park and Lake Bonavista on the way back north.",
    faqs: [
      {
        question: "Are there any airspace limits on flying a drone in Deer Ridge?",
        answer:
          "Few. Deer Ridge sits in uncontrolled Class G airspace, about 13 km from Kirkby Field at Indus, and no authorisation is normally required. The Rockyview General Hospital heliport operates about 5 km away, so flight plans keep clear of its approaches, with the site and altitude confirmed against NAV Drone.",
      },
      {
        question: "Can you photograph a Deer Ridge home that backs onto Fish Creek Provincial Park?",
        answer:
          "Yes. The aircraft launches from the private property and works above the lot line, so the park's valley and forest fill the background without any takeoff or landing on park land, which provincial rules restrict. The result shows the adjacency the fence hides — the reason the lot commands its premium.",
      },
    ],
    primaryKeywords: [
      "drone photography Deer Ridge",
      "aerial photography Deer Ridge",
      "Deer Ridge drone services",
    ],
    nearestLocations: ["deer-run", "willow-park", "lake-bonavista"],
  },

  // ────────────────────────────────────────────────────────────── DEER RUN ──
  {
    slug: "deer-run",
    name: "Deer Run",
    region: "calgary-se",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.9525, lng: -114.0331 },
    tier: 3,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Kirkby Field (Indus)",
      distanceToAerodromeKm: 13,
      authorizationRequired: false,
      authorizationNote:
        "In uncontrolled Class G airspace about 13 km from Kirkby Field (Indus), Deer Run lies outside the charted Calgary-area control zones, and no authorisation is normally required. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Deer Run ends where the valley begins: its outermost streets stop at Fish Creek Provincial Park, with Sikome Lake and the flats around Bow Valley Ranche lying just past the last fence line.",
      consequence:
        "From the ground that adjacency is a fence line; a photographer cannot legally set up inside the park to shoot back, so the lake and the valley — the reason people buy here — stay out of the picture.",
      resolution:
        "The aircraft launches from the property and climbs until Sikome Lake and the valley flats open up past the rooftops, composed entirely from outside the park boundary and delivered in the standard turnaround.",
    },
    landmarks: [
      "Fish Creek Provincial Park",
      "Sikome Lake",
      "Bow Valley Ranche",
      "Deer Run Boulevard SE",
    ],
    terrain:
      "A flat residential pocket pressed against the Fish Creek valley; past the last streets the ground opens into flats, water and cottonwood canopy inside a single wide frame.",
    windNote:
      "Chinook country; the adjacent valley gives some low-level variation, but planning treats Deer Run as exposed — gust spread, not average speed, sets the flight call.",
    seasonalWindow:
      "Open-water months give the strongest frames — lake, flats and canopy in colour beside the community — with a short golden window each fall as the valley turns. Winter reduces the valley to line and shadow, which still composes well.",
    bylawNote:
      "City of Calgary park and pathway bylaws restrict drone launches on municipal land, and provincial park rules govern the adjacent valley, so private-property launch with the client's consent is the default.",
    subjectMix: ["luxury-residential", "municipal-infrastructure"],
    mobilisationNote:
      "About 20 minutes from the base. Comfortably same-day, and a Deer Run flight pairs naturally with Deer Ridge or Douglasdale in a single mobilisation.",
    faqs: [
      {
        question: "Is Deer Run controlled airspace for drone purposes?",
        answer:
          "No. Deer Run lies in uncontrolled Class G airspace, about 13 km from Kirkby Field at Indus, and no airspace authorisation is normally required. What remains is site discipline: the specific address and altitude are still confirmed against NAV Drone before every flight, the same as anywhere else we fly.",
      },
      {
        question: "Can Sikome Lake or Bow Valley Ranche appear in my frames?",
        answer:
          "Yes, as backdrop. Both sit inside Fish Creek Provincial Park, where drone takeoff and landing are restricted, so the aircraft launches from private property and composes toward them from outside the boundary. Water, ranche flats and valley canopy then carry the wide frames without touching park land.",
      },
      {
        question: "What time of year shows Deer Run best?",
        answer:
          "The open-water months. From ice-off through early fall the lake and river flats hold colour and contrast beside the community, and fall turns the valley canopy gold for a short, high-value window. Winter frames still work — the valley reads as clean line and shadow — but the green season sells hardest.",
      },
    ],
    primaryKeywords: [
      "drone photography Deer Run",
      "aerial photography Deer Run",
      "Deer Run drone services",
    ],
    nearestLocations: ["deer-ridge", "douglasdale", "sundance"],
  },

  // ─────────────────────────────────────────────────────── DOUGLASDALE/GLEN ──
  {
    slug: "douglasdale",
    name: "Douglasdale/Glen",
    region: "calgary-se",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.9714, lng: -113.9903 },
    tier: 3,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Kirkby Field (Indus)",
      distanceToAerodromeKm: 10,
      authorizationRequired: false,
      authorizationNote:
        "In uncontrolled Class G airspace about 10 km from Kirkby Field (Indus), Douglasdale/Glen lies outside the charted Calgary-area control zones, and no authorisation is normally required. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Douglasdale/Glen is a bench community: streets like Douglas Rise SE and Douglasbank Way SE hold the flat top of a river terrace, and the ground steps down from the rooflines to Douglasdale Golf Course and the Bow River Pathway on the low side.",
      consequence:
        "At street level the bench is invisible — a terrace-edge home photographs exactly like one in the middle of the grid, and its position above the fairways and the river corridor goes unclaimed.",
      resolution:
        "An elevation hold off the terrace edge layers rooflines, fairways and pathway into one descending frame, flown from a private launch point and delivered in the standard turnaround.",
    },
    landmarks: [
      "Douglasdale Golf Course",
      "Bow River Pathway",
      "Douglas Rise SE",
      "Douglasbank Way SE",
      "Fish Creek Provincial Park",
    ],
    terrain:
      "A river-terrace bench — level on top, with a defined step down to fairways and pathway along the water; the terrace edge gives the southeast one of its cleanest relief lines.",
    windNote:
      "Chinook belt. West wind can turn bumpy along the terrace edge above the river corridor, so bench-edge orbits are flown in the calmer part of the day.",
    seasonalWindow:
      "Fairway season defines the look: from spring green-up until the courses close, the low ground carries saturated colour below the rooflines. Winter flattens the palette, and the terrace edge then works best in low, raking light.",
    bylawNote:
      "City of Calgary bylaws restrict drone takeoff and landing in municipal parks and along pathways, so Douglasdale launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "municipal-infrastructure"],
    mobilisationNote:
      "About 20 minutes from the base. Same-day scheduling is realistic, and Riverbend, Deer Run and McKenzie Lake sit close enough to share the trip.",
    faqs: [
      {
        question: "Do you need permission from anyone to fly a drone in Douglasdale/Glen?",
        answer:
          "Not from an airspace regulator, normally. The community sits in uncontrolled Class G airspace about 10 km from Kirkby Field at Indus, so no authorisation is usually required — the launch simply needs private property with the owner's consent, and the site and altitude are confirmed against NAV Drone before the flight.",
      },
      {
        question: "Can you shoot a home backing onto Douglasdale Golf Course?",
        answer:
          "Yes. The flight stages from the listing itself and works above the property line, so the fairways read as the home's backdrop without launching from course land. A short orbit places the green space, the bench edge and the Bow River Pathway below it into one frame.",
      },
    ],
    primaryKeywords: [
      "drone photography Douglasdale/Glen",
      "aerial photography Douglasdale/Glen",
      "Douglasdale/Glen drone services",
    ],
    nearestLocations: ["riverbend", "deer-run", "mckenzie-lake"],
  },

  // ───────────────────────────────────────────────────────────────── DOVER ──
  {
    slug: "dover",
    name: "Dover",
    region: "calgary-se",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0264, lng: -113.9722 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 11,
      authorizationRequired: true,
      authorizationNote:
        "At about 11 km from Calgary International, Dover runs close to the charted edge of the CYYC control zone — which side of the line an address falls on decides the paperwork. Planning treats it as controlled airspace until NAV Drone shows otherwise, and any required authorisation is filed before the date is booked. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Dover holds the rim above the Bow River Valley — Dover Ridge Drive SE and Valleyview Park sit along the edge — and rim air behaves differently from street air: west wind crossing the valley breaks turbulent exactly where the view is best.",
      consequence:
        "The frames a client wants most — the open valley from the rim — sit exactly where the air is roughest, and an operator who ignores that either delivers shaky footage or scrubs the shoot at the last minute.",
      resolution:
        "Rim work is booked into the calmest slot of the day, with the airspace call made in NAV Drone at booking and any required authorisation filed then — so the still window goes to flying rather than paperwork, and the edit is delivered in the standard turnaround.",
    },
    landmarks: [
      "Dover Ridge Drive SE",
      "Valleyview Park",
      "Bow River Valley",
      "Dover Mercato",
    ],
    terrain:
      "High ground along the rim of the Bow River Valley, falling away sharply past Valleyview Park; above the rim the frame is half rooftops, half open valley.",
    windNote:
      "Chinook belt with a valley rim: west wind crossing the Bow River Valley arrives at the Dover edge with added turbulence, so rim frames take the calmest slot of the day while interior streets are more forgiving.",
    seasonalWindow:
      "Evening is Dover's hour — the westward rim takes golden-hour light across the whole valley frame. Late-summer wildfire smoke can dull that view, so clear-air days are prioritised in that stretch of the season.",
    bylawNote:
      "Municipal bylaws restrict launching and landing in City of Calgary parks, including Valleyview Park, so Dover flights stage from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail"],
    mobilisationNote:
      "About 15 minutes from the base — among the shortest drives on the roster. The airspace check at booking, not the distance, sets the lead time where authorisation is required.",
    faqs: [
      {
        question: "What does sitting at the edge of the CYYC control zone mean for a Dover shoot?",
        answer:
          "It means the chart is read before the calendar. Dover is about 11 km from Calgary International, near the charted boundary of the control zone, so each address is planned as controlled airspace until NAV Drone shows otherwise for that site. Where authorisation is required, it is filed before the date is booked.",
      },
      {
        question: "When is the valley view from Dover best flown?",
        answer:
          "In the calmest slot of the day. West wind crossing the Bow River Valley turns turbulent at the rim where the view is strongest, so rim work is scheduled into still mornings or settled evenings. When the air cooperates, evening light over the valley is the signature frame.",
      },
      {
        question: "Does authorisation delay a Dover booking?",
        answer:
          "Only when the address needs it. The drive is about 15 minutes, so distance is trivial; the deciding factor is which side of the charted boundary the site falls on. That check happens in NAV Drone at booking, any authorisation is filed then, and Dover pairs cleanly with Albert Park or Forest Lawn shoots.",
      },
    ],
    primaryKeywords: [
      "drone photography Dover",
      "aerial photography Dover",
      "Dover drone services",
    ],
    nearestLocations: ["albert-park", "forest-lawn", "erin-woods", "penbrooke-meadows"],
  },

  // ──────────────────────────────────────────────────────────── ERIN WOODS ──
  {
    slug: "erin-woods",
    name: "Erin Woods",
    region: "calgary-se",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0175, lng: -113.9578 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 12,
      authorizationRequired: true,
      authorizationNote:
        "At about 12 km from Calgary International, Erin Woods runs close to the charted edge of the CYYC control zone — which side of the line an address falls on decides the paperwork. Planning treats it as controlled airspace until NAV Drone shows otherwise, and any required authorisation is filed before the date is booked. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "In Erin Woods the aeronautical chart matters more than the street map: the CYYC control zone boundary runs near the community, and two addresses a few blocks apart can fall on different sides of it.",
      consequence:
        "A blanket quote gets Erin Woods wrong in one of two ways: promising a quick routine flight that turns out to need authorisation, or padding every booking with lead time half the community doesn't require.",
      resolution:
        "The address is checked in NAV Drone at booking and planned as controlled airspace until the chart shows otherwise; where authorisation is needed it is filed before the date is confirmed, and delivery runs in the standard turnaround.",
    },
    landmarks: ["Elliston Park", "South Trail Crossing", "Erin Woods Drive SE"],
    terrain:
      "Flat east-side grid; Elliston Park's open water and green space form the largest single feature in the frame, and everything else is roofline pattern.",
    windNote:
      "Chinook-belt wind rules the calendar here as across the city; the flat, open grid gives no shelter, so the gust spread on the day decides the window.",
    seasonalWindow:
      "Long summer evenings put golden hour after working hours, which suits occupied residential shoots; winter's low sun works for pattern frames across the grid, and late-summer smoke is the main contrast risk.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone takeoff and landing on municipal park and pathway land, including Elliston Park, so launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "municipal-infrastructure"],
    mobilisationNote:
      "About 20 minutes from the base. Distance is no constraint; the boundary check in NAV Drone at booking decides whether the date can be immediate or needs authorisation lead time.",
    faqs: [
      {
        question: "Why can two Erin Woods addresses face different drone rules?",
        answer:
          "Because the CYYC control zone boundary passes near the community, about 12 km from the airport. Which side of the charted line an address falls on decides whether authorisation is needed, so every site is checked in NAV Drone and planned as controlled airspace until the chart shows otherwise.",
      },
      {
        question: "How should that boundary shape my Erin Woods booking?",
        answer:
          "Allow for the stricter case. If the address falls inside the zone, the NAV CANADA authorisation is filed before the date is confirmed; if it falls outside, the shoot simply proceeds sooner. Either way the check happens at booking, so the answer arrives before any date is promised.",
      },
    ],
    primaryKeywords: [
      "drone photography Erin Woods",
      "aerial photography Erin Woods",
      "Erin Woods drone services",
    ],
    nearestLocations: ["forest-lawn", "dover", "penbrooke-meadows", "albert-park"],
  },

  // ────────────────────────────────────────────────────────────── FAIRVIEW ──
  {
    slug: "fairview",
    name: "Fairview",
    region: "calgary-se",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.9797, lng: -114.0764 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 16,
      authorizationRequired: true,
      authorizationNote:
        "Fairview shares its airspace corridor with Calgary, about 7 km away, and flights through it are planned as controlled airspace. An Advanced RPAS certificate covers the work, with NAV CANADA RPAS authorisation obtained wherever the specific site requires it. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Fairview is level ground from Fairmount Drive SE to Fairview Drive SE — the aerial frame has no terrain line at all, so composition leans on built mass instead, and the roof plane of Chinook Centre is the largest single shape anywhere near the frame.",
      consequence:
        "On ground this flat there is no vantage point at all — every street-level photo is the same eye-level view, and the context a Fairview client is actually selling, proximity, never enters the frame.",
      resolution:
        "An elevation hold places the property inside its context — the grid around it and the retail mass beside it — under Advanced certification with authorisation obtained wherever the site requires it, delivered in the standard turnaround.",
    },
    landmarks: [
      "Chinook Centre",
      "Fairmount Drive SE",
      "Fairview Drive SE",
      "Riverbend Athletic Park",
    ],
    terrain:
      "Level inner-southeast grid with no terrain line to work with; aerial composition here is built from roof planes, street rhythm and the mass of the retail blocks nearby.",
    windNote:
      "Chinook belt like the rest of the city — no local shelter and no local funnelling, just the west wind's gust spread setting which mornings are flyable.",
    seasonalWindow:
      "Summer canopy softens the grid and winter strips it back to structure; both read well from altitude. The dependable slots are mid-morning calm and the long June golden hours.",
    bylawNote:
      "Drone launches from City of Calgary parks and pathways are restricted under municipal bylaws, so Fairview flights are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail"],
    mobilisationNote:
      "About 15 minutes from the base. Mobilisation is trivial and Fairview pairs easily with Acadia, Willow Park or Lake Bonavista; the authorisation, where required, sets the lead time.",
    faqs: [
      {
        question: "Is Fairview treated as controlled airspace for drone flights?",
        answer:
          "It is planned that way. Fairview sits in the Calgary airspace corridor, so work here runs under an Advanced RPAS certificate, and a NAV CANADA authorisation is obtained wherever the specific site requires one. The address and altitude are confirmed against NAV Drone before every flight.",
      },
      {
        question: "What can altitude add on ground this flat?",
        answer:
          "Context. Fairview's terrain gives a ground camera no vantage at all, so every street-level photo looks alike. From above, the property's position in the grid — and its proximity to Chinook Centre — becomes the frame's argument, which is usually the reason a buyer or tenant is looking here.",
      },
      {
        question: "How quickly can a Fairview shoot happen?",
        answer:
          "The drive is about 15 minutes, so mobilisation is trivial and Fairview pairs easily with Acadia or Willow Park. The variable is authorisation: where the specific site requires one, it is filed at booking and the date is confirmed once it is in hand; after that, wind and light set the schedule.",
      },
    ],
    primaryKeywords: [
      "drone photography Fairview",
      "aerial photography Fairview",
      "Fairview drone services",
    ],
    nearestLocations: ["acadia", "willow-park", "lake-bonavista"],
  },
// ─────────────────────────────────────────────────────────── FOREST LAWN ──
  {
    slug: "forest-lawn",
    name: "Forest Lawn",
    region: "calgary-se",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0197, lng: -113.9847 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 12,
      authorizationRequired: true,
      authorizationNote:
        "Forest Lawn is about 12 km from Calgary International, near the CYYC control zone's charted boundary; a single address can fall either side of the line. The default plan treats it as controlled airspace until NAV Drone confirms otherwise, with authorisation filed before booking wherever it is required. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "17 Avenue SE runs almost due east–west across a flat grid, so in the last hour of the day the sun travels straight down the avenue and front-lights the storefronts of the International Avenue BRZ for blocks at a stretch.",
      consequence:
        "A commercial shoot booked for midday throws that away — the avenue flattens, awnings shade the frontages, and the streetscape a business is paying to show reads as any strip in the city.",
      resolution:
        "The flight is scheduled into that last hour, with an elevated hold looking down the avenue while the light is on the storefronts, delivered in the standard turnaround.",
    },
    landmarks: ["17 Avenue SE", "International Avenue BRZ", "Elliston Park", "44 Street SE"],
    terrain:
      "Flat east-side prairie grid — long straight avenues and consistent rooflines, with the open ground of Elliston Park breaking the pattern at the eastern edge.",
    windNote:
      "Chinook belt, and with no valley or ridge on the east-side grid to break it, the west wind arrives at full strength. The gust spread, not the average speed, is the go or no-go number.",
    seasonalWindow:
      "The grid is flat and largely unshaded, so the light window runs the season's full length — short winter afternoons, long June evenings, and late-summer wildfire smoke as the main threat to contrast.",
    bylawNote:
      "City of Calgary bylaws restrict drone takeoff and landing in municipal parks and on pathways, so launches are staged from private property with the client's consent.",
    subjectMix: ["commercial-retail", "municipal-infrastructure", "construction-progress"],
    mobilisationNote:
      "About 15 minutes from the base. Same-day work is realistic when weather and authorisation align, and one trip pairs naturally with Albert Park, Dover or Penbrooke Meadows.",
    faqs: [
      {
        question: "Does a drone shoot in Forest Lawn need NAV CANADA authorisation?",
        answer:
          "Often, yes. Forest Lawn sits about 12 km from Calgary International, near the charted edge of the CYYC control zone, so which side of the line an address falls on decides the paperwork. Fly4MEdia plans it as controlled airspace until NAV Drone shows otherwise, and files any authorisation before booking the date.",
      },
      {
        question: "When is the best light for aerial photography along International Avenue?",
        answer:
          "The last hour of the day. 17 Avenue SE runs east–west, so the low evening sun travels straight down its length and front-lights the storefronts of the International Avenue BRZ. Midday light flattens the streetscape, so a golden-hour flight is scheduled whenever the forecast allows it.",
      },
      {
        question: "Can Forest Lawn be paired with nearby communities in one booking?",
        answer:
          "Yes. At about 15 minutes from the base, Forest Lawn pairs naturally with Albert Park, Dover, Erin Woods or Penbrooke Meadows. One mobilisation can cover a listing and a commercial property across two communities in a single light window, which is the efficient way to book this corridor.",
      },
    ],
    primaryKeywords: [
      "drone photography Forest Lawn",
      "aerial photography Forest Lawn",
      "Forest Lawn drone services",
    ],
    nearestLocations: ["albert-park", "dover", "erin-woods", "penbrooke-meadows"],
  },

  // ───────────────────────────────────────────────────────────── INGLEWOOD ──
  {
    slug: "inglewood",
    name: "Inglewood",
    region: "calgary-se",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0375, lng: -114.0225 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 9,
      authorizationRequired: true,
      authorizationNote:
        "Inglewood lies inside the charted CYYC control zone, about 9 km from Calgary International. Work here flies under an Advanced RPAS certificate, and the NAV CANADA RPAS authorisation is in hand before a shoot date is confirmed. Peter Lougheed Centre heliport operates about 5 km away, and flight plans keep clear of its approaches. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Peter Lougheed Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Inglewood's airspace is spoken for twice over: the neighbourhood sits about 9 km from Calgary International inside the CYYC control zone, and the Peter Lougheed Centre heliport operates about 5 km away.",
      consequence:
        "That geometry rules out the casual operator entirely — a client who books one loses the date when the flight cannot be made legal, usually after the listing has already gone live without aerials.",
      resolution:
        "Advanced RPAS certification means the NAV CANADA authorisation is filed as part of booking, the heliport approaches are plotted first, and the shoot is confirmed only once the airspace is secured — then delivered in the standard turnaround.",
    },
    landmarks: [
      "9 Avenue SE",
      "Inglewood Bird Sanctuary",
      "Pearce Estate Park",
      "Fort Calgary",
      "National Music Centre",
    ],
    terrain:
      "River-flat streets held in a bend of the river, with Pearce Estate Park and the Inglewood Bird Sanctuary lining the bank and the 9 Avenue SE commercial strip running the spine.",
    windNote:
      "Chinook belt like the rest of the corridor. The flats sit low, but a chinook's gust spread reaches ground level everywhere — wind is read on site, not assumed from a city forecast.",
    seasonalWindow:
      "Leaf-out changes this neighbourhood more than most: summer canopy softens the older streets, and autumn colour along the river parks is Inglewood's best aerial season. Late-summer smoke is the main contrast risk.",
    bylawNote:
      "City of Calgary parks bylaws restrict takeoff and landing in municipal parks, including the river parks, so flights launch from private property with the owner's permission.",
    subjectMix: ["commercial-retail", "luxury-residential", "construction-progress"],
    mobilisationNote:
      "About 15 minutes from the base. Once the authorisation is in hand it is an easy same-day mobilisation, and it pairs well with Ramsay or Ogden in a single trip.",
    faqs: [
      {
        question: "Is it legal to fly a drone in Inglewood?",
        answer:
          "Yes, with the right certification. Inglewood sits about 9 km from Calgary International, inside the CYYC Class C control zone, so flights need an Advanced RPAS certificate and a NAV CANADA authorisation. Fly4MEdia holds the certificate, files the authorisation before confirming the date, and keeps clear of the Peter Lougheed Centre heliport approaches.",
      },
      {
        question: "Do you fly over the Inglewood Bird Sanctuary?",
        answer:
          "No. The sanctuary is a wildlife refuge, and flight plans here are drawn to keep clear of it as well as the charted heliport approaches. Riverside frames are built from angles over the client's own property, so the shot list never depends on airspace the flight cannot use.",
      },
      {
        question: "How much notice does an Inglewood shoot need?",
        answer:
          "Enough to secure the NAV CANADA authorisation before the date is confirmed — that is filed as part of booking, not left to the morning of the shoot. The drive itself is about 15 minutes, so once the paperwork is in hand, weather is the only variable that moves the date.",
      },
    ],
    primaryKeywords: [
      "drone photography Inglewood",
      "aerial photography Inglewood",
      "Inglewood drone services",
    ],
    nearestLocations: ["ramsay", "ogden", "bridgeland-riverside"],
  },

  // ──────────────────────────────────────────────────────── LAKE BONAVISTA ──
  {
    slug: "lake-bonavista",
    name: "Lake Bonavista",
    region: "calgary-se",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.9611, lng: -114.0658 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Kirkby Field (Indus)",
      distanceToAerodromeKm: 15,
      authorizationRequired: false,
      authorizationNote:
        "Lake Bonavista lies outside the charted Calgary-area control zones in uncontrolled Class G airspace, about 15 km from Kirkby Field (Indus). No airspace authorisation is normally required. Flight plans here also keep clear of Rockyview General Hospital heliport, operating about 4 km away. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Rockyview General Hospital heliport"],
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "From the air Lake Bonavista changes subject twice a year — open water that carries the sky all summer, then a frozen white plane that erases the shoreline once the lake ices over.",
      consequence:
        "A lakefront listing shot in the wrong half of that cycle sells the wrong lake: grey ice behind a home whose asking price is built on summer water, or a heat-flattened frame when winter texture was the point.",
      resolution:
        "The shoot is timed to the water — ice-off through freeze-up for open-water frames, with a golden-hour hold that keeps low sun on the surface — and delivered in the standard turnaround.",
    },
    landmarks: [
      "Lake Bonavista (private lake)",
      "Lake Bonavista Drive SE",
      "Bonavista Village",
      "Southcentre Mall",
    ],
    terrain:
      "A flat, established lake-community plan — crescents and cul-de-sacs ringing a private lake, with Bonavista Village and Southcentre Mall anchoring the commercial edge.",
    windNote:
      "Chinook belt. The lake sits open to the west wind, and open water shows every gust as surface texture that reads in video, so calm evenings are worth waiting for.",
    seasonalWindow:
      "The lake sets the calendar: ice-off in spring opens the season and freeze-up closes it. Low evening light on open water is the community's signature frame; winter flips the lake into a white field.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone takeoff and landing on municipal park land and pathways, so launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail"],
    mobilisationNote:
      "About 20 minutes from the base — comfortably a same-day booking, and one trip covers Willow Park, Acadia or Fairview alongside it.",
    faqs: [
      {
        question: "Do you need airspace clearance to fly over Lake Bonavista?",
        answer:
          "Normally, no. Lake Bonavista sits in uncontrolled Class G airspace outside the charted Calgary control zones, about 15 km from Kirkby Field at Indus. Rockyview General Hospital's heliport operates about 4 km away, so flight plans keep clear of its approaches, and every site is confirmed against NAV Drone before the flight.",
      },
      {
        question: "What season shows a Lake Bonavista lakefront listing at its best?",
        answer:
          "Open water — between ice-off in spring and freeze-up in late fall. That is when the lake reads blue and the private shoreline reads as an amenity. In winter the lake photographs as a snowfield, which suits some frames but sells a different story than most listings want.",
      },
      {
        question: "The lake is private — can you still photograph it?",
        answer:
          "Yes. Launch and landing are staged from the client's own property with their consent, and the flight plan frames the client's shoreline rather than the neighbours'. The lake itself appears as context in the frame; the flight never needs to touch anyone else's land to get it.",
      },
    ],
    primaryKeywords: [
      "drone photography Lake Bonavista",
      "aerial photography Lake Bonavista",
      "Lake Bonavista drone services",
    ],
    nearestLocations: ["willow-park", "acadia", "fairview"],
  },

  // ──────────────────────────────────────────────────────────────── LEGACY ──
  {
    slug: "legacy",
    name: "Legacy",
    region: "calgary-se",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.8611, lng: -113.9769 },
    tier: 3,
    driveMinutesFromCalgary: 30,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Kirkby Field (Indus)",
      distanceToAerodromeKm: 11,
      authorizationRequired: false,
      authorizationNote:
        "In uncontrolled Class G airspace about 11 km from Kirkby Field (Indus), Legacy lies outside the charted Calgary-area control zones, and no authorisation is normally required. Flight plans here also keep clear of South Health Campus heliport, operating about 3 km away. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["South Health Campus heliport"],
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Legacy ends not at a fence line but at a fall of ground — the community's edge drops toward Pine Creek, and the Legacy Environmental Reserve keeps that slope unbuilt.",
      consequence:
        "A reserve-backing lot and an interior lot look identical from the street, so ground photography erases the exact difference the seller paid a premium for at purchase.",
      resolution:
        "A boundary-line orbit along the reserve edge, or a rising reveal off the lot toward Pine Creek, puts that fall of land into the frame — delivered in the standard turnaround.",
    },
    landmarks: [
      "Legacy Environmental Reserve",
      "Pine Creek",
      "Legacy Village",
      "Township Shopping",
      "Legacy Boulevard SE",
    ],
    terrain:
      "New-build grid on high prairie at the city's southern edge, falling away to Pine Creek along the community's rim, where the Legacy Environmental Reserve holds the slope as open ground.",
    windNote:
      "Chinook belt, and the southern edge is fully exposed to it. The Pine Creek ravine can bend gusts along its own line, so the reserve edge is flown with more margin than the interior streets.",
    seasonalWindow:
      "Green-up reaches the reserve grasses before the new lawns, and autumn browns them first — the shoulder seasons are when the reserve edge reads most distinctly. North-facing ravine slopes hold snow after the grid is clear.",
    bylawNote:
      "City of Calgary bylaws restrict launching and landing in municipal parks and reserves, so takeoff is planned from private property with the owner's permission.",
    subjectMix: ["new-build-subdivision", "construction-progress", "commercial-retail"],
    mobilisationNote:
      "About 30 minutes from the base — still a single-trip booking, and it pairs efficiently with Walden, Seton or Chaparral on the same run south.",
    faqs: [
      {
        question: "Are there airspace restrictions on drone flights in Legacy?",
        answer:
          "Few. Legacy lies in uncontrolled Class G airspace outside the charted Calgary control zones, about 11 km from Kirkby Field at Indus, so no authorisation is normally required. The South Health Campus heliport operates about 3 km away; flight plans keep clear of its approaches, and every site is confirmed against NAV Drone first.",
      },
      {
        question: "Can an aerial show that a Legacy lot backs onto the environmental reserve?",
        answer:
          "Yes — that is the frame Legacy sellers usually need. A boundary-line orbit or an elevated frame over the lot places the fence line against the Legacy Environmental Reserve and the Pine Creek slope beyond it, which is a relationship no ground-level photograph can establish.",
      },
    ],
    primaryKeywords: [
      "drone photography Legacy",
      "aerial photography Legacy",
      "Legacy drone services",
    ],
    nearestLocations: ["walden", "seton", "chaparral"],
  },

  // ────────────────────────────────────────────────────────────── MAHOGANY ──
  {
    slug: "mahogany",
    name: "Mahogany",
    region: "calgary-se",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.8933, lng: -113.9308 },
    tier: 3,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Kirkby Field (Indus)",
      distanceToAerodromeKm: 6,
      authorizationRequired: false,
      authorizationNote:
        "In uncontrolled Class G airspace about 6 km from Kirkby Field (Indus), Mahogany lies outside the charted Calgary-area control zones, and no authorisation is normally required. South Health Campus heliport operates about 2 km away, and flight plans keep clear of its approaches. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["South Health Campus heliport"],
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Mahogany is wrapped around two different waters: an engineered lake that stays groomed and blue all season, and the Mahogany Wetlands, which run a wilder calendar — silver at spring melt, green through summer, copper reeds by fall.",
      consequence:
        "A shoot booked by convenience rather than by that calendar can put brown reed beds behind a beach-club lifestyle pitch, or miss the weeks when the wetlands are the best-looking thing in the frame.",
      resolution:
        "Lake frames are flown between ice-off and freeze-up with a golden-hour hold on the water, and wetland frames are timed to their season — both delivered in the standard turnaround.",
    },
    landmarks: [
      "Mahogany Lake",
      "Mahogany Beach Club",
      "Mahogany Wetlands",
      "Mahogany Village Market",
    ],
    terrain:
      "Flat southeast prairie built around an engineered lake and a natural wetland — beach frontage on one side of the plan, reed beds on the other, with Mahogany Village Market as the commercial node.",
    windNote:
      "Chinook belt with nothing between the community and the west wind. The lake surface telegraphs every gust in video, so calm evenings are chosen for water frames.",
    seasonalWindow:
      "Ice-off to freeze-up defines the lake season; the wetlands peak twice, at spring melt and again when the reeds turn in early autumn. Late-summer smoke is the main contrast risk.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone takeoff and landing in municipal parks and on pathways, so launches here are staged from private property with the client's consent.",
    subjectMix: [
      "new-build-subdivision",
      "luxury-residential",
      "commercial-retail",
      "construction-progress",
    ],
    mobilisationNote:
      "About 25 minutes from the base — an easy same-day booking, and one southeast run covers Auburn Bay, Seton or Cranston in the same light window.",
    faqs: [
      {
        question: "Does Mahogany's distance from Kirkby Field affect a drone shoot?",
        answer:
          "Not materially. Mahogany sits in uncontrolled Class G airspace about 6 km from Kirkby Field at Indus, and no authorisation is normally required. The aerodrome and the South Health Campus heliport, about 2 km away, are both kept clear of in planning, and each site is confirmed against NAV Drone before flight.",
      },
      {
        question: "When should a Mahogany lakefront shoot be booked?",
        answer:
          "For open water — from ice-off in spring to freeze-up in late fall, when Mahogany Lake and the beach club read the way the community markets itself. The Mahogany Wetlands are a second subject with their own calendar: silver at melt, green in summer, copper reeds in autumn.",
      },
    ],
    primaryKeywords: [
      "drone photography Mahogany",
      "aerial photography Mahogany",
      "Mahogany drone services",
    ],
    nearestLocations: ["auburn-bay", "seton", "cranston"],
  },

  // ─────────────────────────────────────────────────────────── MAPLE RIDGE ──
  {
    slug: "maple-ridge",
    name: "Maple Ridge",
    region: "calgary-se",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.9767, lng: -114.0347 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 16,
      authorizationRequired: true,
      authorizationNote:
        "Maple Ridge sits in the same airspace corridor as Calgary, about 8 km away, where flights are planned as controlled airspace. Work here is conducted under an Advanced RPAS certificate, with a NAV CANADA RPAS authorisation obtained wherever the specific site requires one. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "The last light of the day reaches Maple Ridge across the open ground of Fish Creek Provincial Park, so the neighbourhood's mature canopy takes low, raking side-light at exactly the hour most shoots have already packed up.",
      consequence:
        "Booked at midday, that canopy becomes the problem instead of the asset — flat overhead light turns the trees into a dark mat that swallows rooflines and hides the streets beneath them.",
      resolution:
        "The flight is held for the final hour, with a golden-hour hold above the canopy and a rising reveal that lifts from street level to the treetops — delivered in the standard turnaround.",
    },
    landmarks: [
      "Fish Creek Provincial Park",
      "Maple Ridge Ravine",
      "Maple Ridge Drive SE",
      "Lake Bonavista Village",
    ],
    terrain:
      "Mature-canopy streets on the tableland north of Fish Creek Provincial Park, with the Maple Ridge Ravine cutting toward the valley and big trees closing over much of the grid.",
    windNote:
      "Chinook belt. The Maple Ridge Ravine can channel gusts along its line toward the valley, so the ravine edge is flown with more margin than the sheltered interior streets.",
    seasonalWindow:
      "Autumn is this neighbourhood's aerial season — the canopy turns, and low light through the colour is worth planning a listing around. In full leaf, summer frames are scheduled to the evening; bare-branch winter opens up the rooflines.",
    bylawNote:
      "City of Calgary parks bylaws restrict launch and landing on municipal park land, and provincial park rules govern Fish Creek, so takeoff is staged from private property with consent.",
    subjectMix: ["luxury-residential", "commercial-retail"],
    mobilisationNote:
      "About 20 minutes from the base. Comfortably same-day once any authorisation is in hand, and it pairs naturally with Lake Bonavista, Willow Park, Acadia or Deer Run.",
    faqs: [
      {
        question: "What paperwork applies to a drone flight in Maple Ridge?",
        answer:
          "Maple Ridge is planned as controlled airspace in the Calgary corridor. Work here runs under an Advanced RPAS certificate, with a NAV CANADA authorisation obtained wherever the specific site requires one, and the site and altitude are confirmed against NAV Drone before every flight — all handled before the date is booked.",
      },
      {
        question: "Why does the time of day matter so much for Maple Ridge aerials?",
        answer:
          "Because of the canopy. Maple Ridge's mature trees swallow flat midday light and hide the rooflines beneath them. In the last hour the sun comes in low across the open ground of Fish Creek Provincial Park and rakes through the canopy instead of sitting on top of it.",
      },
      {
        question: "Can a Maple Ridge shoot include Fish Creek Provincial Park?",
        answer:
          "The park appears as context in elevated frames, but provincial park rules govern the land itself, so launches are staged from private property outside it. Framing the valley from the neighbourhood's edge shows the relationship buyers care about without the flight depending on park ground.",
      },
    ],
    primaryKeywords: [
      "drone photography Maple Ridge",
      "aerial photography Maple Ridge",
      "Maple Ridge drone services",
    ],
    nearestLocations: ["lake-bonavista", "willow-park", "acadia", "deer-run"],
  },

  // ───────────────────────────────────────────────────────── MCKENZIE LAKE ──
  {
    slug: "mckenzie-lake",
    name: "McKenzie Lake",
    region: "calgary-se",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.9269, lng: -113.9917 },
    tier: 3,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Kirkby Field (Indus)",
      distanceToAerodromeKm: 9,
      authorizationRequired: false,
      authorizationNote:
        "McKenzie Lake sits in uncontrolled Class G airspace, clear of the charted Calgary-area control zones and about 9 km from Kirkby Field (Indus). Authorisation is not normally required here. South Health Campus heliport operates about 6 km away, and flight plans keep clear of its approaches. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["South Health Campus heliport"],
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Nearly every street here answers to the same name — McKenzie Lake Boulevard feeding its ways, greens, manors and bays — and from the air that naming becomes geometry: one collector looping the lake, courts branching toward the water.",
      consequence:
        "On the ground those courts are interchangeable, so a listing photo cannot tell a buyer the one thing the plan is built around — where the address sits relative to the lake and to Fish Creek Provincial Park.",
      resolution:
        "An elevated placing frame or a slow orbit reads the whole loop at once and fixes the home's position on it, delivered in the standard turnaround.",
    },
    landmarks: [
      "McKenzie Lake (private)",
      "McKenzie Lake Boulevard SE",
      "Fish Creek Provincial Park",
      "Blue Devil Golf Club",
    ],
    terrain:
      "A single looped plan on the bench above Fish Creek Provincial Park — courts stepping toward a private lake, with the fairways of the Blue Devil Golf Club as open green ground nearby.",
    windNote:
      "Chinook belt. West wind spills over the Fish Creek valley edge onto the bench, so the rim of the community is gustier than its sheltered interior courts.",
    seasonalWindow:
      "Open water between ice-off and freeze-up is the lake's season; the valley and fairways green up early and turn first in fall. Winter reads clean and graphic when fresh snow is on the loop.",
    bylawNote:
      "City of Calgary bylaws restrict drone takeoff and landing in municipal parks and pathways, and provincial rules apply in Fish Creek Provincial Park, so launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "municipal-infrastructure"],
    mobilisationNote:
      "About 20 minutes from the base — a straightforward same-day booking, easily paired with Douglasdale, McKenzie Towne or Copperfield on one run.",
    faqs: [
      {
        question: "Is McKenzie Lake inside controlled airspace?",
        answer:
          "No. McKenzie Lake lies in uncontrolled Class G airspace outside the charted Calgary control zones, about 9 km from Kirkby Field at Indus, so no authorisation is normally required. The South Health Campus heliport operates about 6 km away and is kept clear of, and every site is confirmed against NAV Drone.",
      },
      {
        question: "How do you show where a home sits relative to the lake and Fish Creek?",
        answer:
          "With an elevated placing frame or a slow orbit. The community is one loop — McKenzie Lake Boulevard with its ways, greens and bays branching off — so a single aerial can place an address between the private lake and Fish Creek Provincial Park in a way no street photo can.",
      },
    ],
    primaryKeywords: [
      "drone photography McKenzie Lake",
      "aerial photography McKenzie Lake",
      "McKenzie Lake drone services",
    ],
    nearestLocations: ["douglasdale", "mckenzie-towne", "copperfield"],
  },

  // ──────────────────────────────────────────────────────── MCKENZIE TOWNE ──
  {
    slug: "mckenzie-towne",
    name: "McKenzie Towne",
    region: "calgary-se",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.9214, lng: -113.9631 },
    tier: 3,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Kirkby Field (Indus)",
      distanceToAerodromeKm: 7,
      authorizationRequired: false,
      authorizationNote:
        "McKenzie Towne lies outside the charted Calgary-area control zones in uncontrolled Class G airspace, about 7 km from Kirkby Field (Indus). No airspace authorisation is normally required. South Health Campus heliport operates about 4 km away, and flight plans keep clear of its approaches. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["South Health Campus heliport"],
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "In autumn the street trees along Prestwick Boulevard and Elgin Boulevard turn before the lawns fade, and for those few weeks the community's plan draws itself from the air — tree-lined boulevards in colour, converging on High Street.",
      consequence:
        "The plan is what McKenzie Towne sells, and a shoot flown in flat mid-winter or high summer loses it — the boulevards blend into the rooftops and the community photographs like any subdivision around it.",
      resolution:
        "Where the calendar allows, the flight is timed to that turn, with an elevated frame down the boulevard toward the High Street Shops — delivered in the standard turnaround.",
    },
    landmarks: [
      "Prestwick Boulevard SE",
      "Elgin Boulevard SE",
      "High Street Shops",
      "Inverness Pond",
      "McKenzie Towne Hall",
    ],
    terrain:
      "A new-urbanist plan of tree-lined boulevards and greens converging on High Street, with Inverness Pond as the water anchor and open prairie running to the community's edge.",
    windNote:
      "Chinook belt on open southeast prairie — no valley or ridge breaks the west wind here, so the gust spread on the day decides the flight, not the seasonal average.",
    seasonalWindow:
      "Boulevard leaf-out through the autumn turn is the flattering window; Inverness Pond holds open water between spring and freeze-up and reads as a white oval in winter.",
    bylawNote:
      "City of Calgary parks bylaws restrict takeoff and landing on municipal park land and pathways, so flights are launched from private property with the owner's consent.",
    subjectMix: ["commercial-retail", "new-build-subdivision", "luxury-residential"],
    mobilisationNote:
      "About 25 minutes from the base — an easy same-day booking, and one trip pairs it with McKenzie Lake, Copperfield or New Brighton.",
    faqs: [
      {
        question: "Do drone flights in McKenzie Towne require authorisation?",
        answer:
          "Normally, no. McKenzie Towne sits in uncontrolled Class G airspace, about 7 km from Kirkby Field at Indus and outside the charted Calgary control zones. The South Health Campus heliport, about 4 km away, is kept clear of in planning, and every site is confirmed against NAV Drone before flight.",
      },
      {
        question: "Can you photograph High Street and the commercial areas from the air?",
        answer:
          "Yes. Elevated frames down the boulevards show how Prestwick and Elgin converge on High Street, which is the community's whole design argument. Launches are staged from private property with the owner's consent, so a retail client's own site is usually the staging point for the flight.",
      },
    ],
    primaryKeywords: [
      "drone photography McKenzie Towne",
      "aerial photography McKenzie Towne",
      "McKenzie Towne drone services",
    ],
    nearestLocations: ["mckenzie-lake", "copperfield", "new-brighton"],
  },

  // ────────────────────────────────────────────────────────── NEW BRIGHTON ──
  {
    slug: "new-brighton",
    name: "New Brighton",
    region: "calgary-se",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.9188, lng: -113.9469 },
    tier: 3,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Kirkby Field (Indus)",
      distanceToAerodromeKm: 6,
      authorizationRequired: false,
      authorizationNote:
        "New Brighton lies outside the charted Calgary-area control zones in uncontrolled Class G airspace, about 6 km from Kirkby Field (Indus). No airspace authorisation is normally required. South Health Campus heliport operates about 4 km away, and flight plans keep clear of its approaches. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["South Health Campus heliport"],
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "New Brighton's streetscape is still young — the trees have not yet cleared the rooflines — so from the air the community's texture comes almost entirely from the angle of the light.",
      consequence:
        "A midday aerial here looks like a midday aerial of every young southeast community: flat roofs, flat lawns, no shadows to separate one plane from the next, and nothing that says this address.",
      resolution:
        "The flight is booked into the last hour of sun, when low light gives every roof plane and fence line a shadow to be seen by — a golden-hour hold, delivered in the standard turnaround.",
    },
    landmarks: [
      "New Brighton Pond",
      "New Brighton Club",
      "Shepard Pathway",
      "New Brighton Drive SE",
    ],
    terrain:
      "Low-relief new-build plan gathered around New Brighton Pond, with the Shepard Pathway threading the community and open prairie past the southeastern edge.",
    windNote:
      "Chinook belt on open prairie, and the young streetscape offers no windbreak yet — the west wind arrives at ground level much as the forecast gives it aloft.",
    seasonalWindow:
      "With little canopy, colour depends on the ground: green-up through early autumn is the flattering window, and low winter sun over fresh snow gives clean, graphic frames.",
    bylawNote:
      "City of Calgary bylaws restrict drone launch and landing in municipal parks and along pathways, so takeoff is staged from private property with the client's consent.",
    subjectMix: ["new-build-subdivision", "construction-progress", "commercial-retail"],
    mobilisationNote:
      "About 25 minutes from the base — same-day work is straightforward, and McKenzie Towne, Copperfield or McKenzie Lake pair with it on a single southeast run.",
    faqs: [
      {
        question: "What airspace rules cover New Brighton?",
        answer:
          "New Brighton lies in uncontrolled Class G airspace, about 6 km from Kirkby Field at Indus and outside the charted Calgary control zones, so no authorisation is normally required. Flight plans keep clear of the South Health Campus heliport about 4 km away, and each site is confirmed against NAV Drone.",
      },
      {
        question: "Why are New Brighton shoots booked late in the day?",
        answer:
          "Because the streetscape is young. With trees still below the rooflines, the community has little built-in texture, and flat midday light makes every frame look the same. The last hour of sun gives each roof plane and fence line a shadow, which is what separates the listing from the street.",
      },
    ],
    primaryKeywords: [
      "drone photography New Brighton",
      "aerial photography New Brighton",
      "New Brighton drone services",
    ],
    nearestLocations: ["mckenzie-towne", "copperfield", "mckenzie-lake"],
  },

  // ───────────────────────────────────────────────────────────────── OGDEN ──
  {
    slug: "ogden",
    name: "Ogden",
    region: "calgary-se",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0136, lng: -114.0206 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 12,
      authorizationRequired: true,
      authorizationNote:
        "Ogden sits about 12 km from Calgary International — near the charted boundary of the CYYC control zone, close enough that which side of the line a given address falls on decides the paperwork. It is planned as controlled airspace until NAV Drone shows otherwise for the specific site, and where authorisation is required it is filed before the date is booked. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Ogden's best aerial subject changes with the river: in winter the reach through Carburn Park holds stretches of open water that read nearly black against the snow, and by summer the same reach turns into a green corridor along the Bow River Pathway.",
      consequence:
        "For a property near the park, the booking date quietly decides whether the river reads as an amenity or as an ice field — and a shoot planned without the river in mind wastes the neighbourhood's strongest feature.",
      resolution:
        "The flight is timed to the river's state, with an approach run along the pathway corridor toward the property, and any control-zone authorisation is filed before the date is booked — delivered in the standard turnaround.",
    },
    landmarks: [
      "Carburn Park",
      "Bow River Pathway",
      "Ogden Road SE",
      "Riverbend Co-op",
    ],
    terrain:
      "An older grid on the flats east of the river, with Carburn Park's river frontage and the Bow River Pathway running the western edge of the community.",
    windNote:
      "Chinook belt. The river corridor past Carburn Park can bend the wind along its own line, so conditions at the park edge are read separately from the interior grid.",
    seasonalWindow:
      "Winter open water gives the strongest contrast frames along the river; summer turns the corridor green, and autumn colours the bank trees first. Late-summer smoke is the main risk to clarity.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone takeoff and landing in municipal parks and on the pathway system, so launches are staged from private property with the client's consent.",
    subjectMix: ["commercial-retail", "municipal-infrastructure", "construction-progress"],
    mobilisationNote:
      "About 15 minutes from the base — one of the closest communities in the corridor, and an easy pairing with Ramsay, Riverbend or Douglasdale in a single window.",
    faqs: [
      {
        question: "Which side of the CYYC control-zone boundary is Ogden on?",
        answer:
          "Close enough that it depends on the address. Ogden sits about 12 km from Calgary International, near the charted edge of the control zone, so Fly4MEdia plans every flight as controlled airspace until NAV Drone shows otherwise for the specific site, and files any required authorisation before the date is booked.",
      },
      {
        question: "What time of year photographs best along the river in Ogden?",
        answer:
          "Both ends of it, differently. In winter the river through Carburn Park holds stretches of open water that read dark against the snow; in summer the same reach becomes a green corridor along the Bow River Pathway. The season is chosen to match what the frame needs to say.",
      },
      {
        question: "Is Ogden practical for a short-notice booking?",
        answer:
          "The drive is about 15 minutes, so mobilisation is easy — the constraint is the airspace check, which is done before the date is confirmed rather than on the morning. Ogden also pairs naturally with Ramsay or Riverbend when two properties need covering in one light window.",
      },
    ],
    primaryKeywords: [
      "drone photography Ogden",
      "aerial photography Ogden",
      "Ogden drone services",
    ],
    nearestLocations: ["ramsay", "riverbend", "douglasdale"],
  },

  // ──────────────────────────────────────────────────────────────── RAMSAY ──
  {
    slug: "ramsay",
    name: "Ramsay",
    region: "calgary-se",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.036, lng: -114.055 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 10,
      authorizationRequired: true,
      authorizationNote:
        "About 10 km from Calgary International, Ramsay falls inside the CYYC control zone as charted. An Advanced RPAS certificate covers the operation, with the NAV CANADA RPAS authorisation secured before the booking is confirmed. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Ramsay puts a century of rooflines on a single grid — pre-war cottages, warehouse brick and new infill climbing the same slope to Scotsman's Hill, with Stampede Park spread out below the brow.",
      consequence:
        "That mix is the neighbourhood's whole argument, and ground photography crops it to one lot at a time — the slope, the skyline relationship and the drop to Stampede Park all vanish at street level.",
      resolution:
        "Elevated frames along the grid and a rising reveal off the hill's edge hold the mix and the drop in one composition, with the NAV CANADA authorisation filed before the shoot is confirmed — delivered in the standard turnaround.",
    },
    landmarks: [
      "Scotsman's Hill",
      "Stampede Park",
      "Elbow River Pathway",
      "Spiller Road SE",
      "Repsol Sport Centre",
    ],
    terrain:
      "A gridded hillside rising from the flats along the Elbow River Pathway to Scotsman's Hill, where the ground falls away abruptly toward Stampede Park.",
    windNote:
      "Chinook belt, and the brow of Scotsman's Hill takes the west wind first — the exposed hilltop edge is read separately from the sheltered streets behind it.",
    seasonalWindow:
      "The view frames off Scotsman's Hill face west, so evening light trades detail for skyline silhouette while morning light front-lights the slope and its rooflines. Chinooks strip snow from this grid quickly.",
    bylawNote:
      "City of Calgary parks bylaws restrict launching and landing on municipal park land, so takeoff and landing are planned from private property with the owner's permission.",
    subjectMix: ["luxury-residential", "construction-progress", "commercial-retail"],
    mobilisationNote:
      "About 15 minutes from the base. Once the authorisation is in hand it is an easy same-day mobilisation, pairing naturally with Inglewood, Ogden or Erin Woods.",
    faqs: [
      {
        question: "Can you fly a drone in Ramsay, so close to Stampede Park?",
        answer:
          "Yes, with authorisation. Ramsay sits about 10 km from Calgary International inside the CYYC Class C control zone, so flights run under an Advanced RPAS certificate with a NAV CANADA authorisation filed before the shoot is confirmed. When an event changes the airspace picture, the plan is re-checked against NAV Drone.",
      },
      {
        question: "What does an aerial show in Ramsay that ground photography cannot?",
        answer:
          "The mix and the drop. A century of rooflines — cottages, brick and new infill — shares one grid on the slope, and the ground falls away from Scotsman's Hill toward Stampede Park. An elevated frame holds both in view; a street-level photo crops the story to a single lot.",
      },
      {
        question: "How quickly can a Ramsay shoot be scheduled?",
        answer:
          "The drive is about 15 minutes, so the timeline is set by the NAV CANADA authorisation, which is filed as part of booking. Once it is in hand, weather is the only variable. Ramsay pairs well with Inglewood or Ogden when a second property is on the list.",
      },
    ],
    primaryKeywords: [
      "drone photography Ramsay",
      "aerial photography Ramsay",
      "Ramsay drone services",
    ],
    nearestLocations: ["inglewood", "ogden", "erin-woods"],
  },

  // ───────────────────────────────────────────────────────────── RIVERBEND ──
  {
    slug: "riverbend",
    name: "Riverbend",
    region: "calgary-se",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.9903, lng: -113.9928 },
    tier: 3,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Kirkby Field (Indus)",
      distanceToAerodromeKm: 12,
      authorizationRequired: false,
      authorizationNote:
        "Riverbend lies outside the charted Calgary-area control zones in uncontrolled Class G airspace, about 12 km from Kirkby Field (Indus). No airspace authorisation is normally required. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "From the air Riverbend resolves into two networks: the street plan branching off Riverbend Drive, and the Bow River Pathway tracing the park edge past Carburn Park — parallel systems separated by only a band of backyards.",
      consequence:
        "The distance between those two networks is exactly what a buyer wants to know, and it is the one measurement ground photography cannot show — a street photo cannot say how close the pathway and the park actually are.",
      resolution:
        "An elevated placing frame sets the address between its street and the pathway with Carburn Park beyond — and with routine airspace here there is no authorisation to wait on, so the flight is scheduled to the light and delivered in the standard turnaround.",
    },
    landmarks: [
      "Riverbend Drive SE",
      "Carburn Park",
      "Bow River Pathway",
      "Riverbend Co-op",
    ],
    terrain:
      "A river-flat suburb inside a bend of the river — streets running off Riverbend Drive toward the water, with Carburn Park and the Bow River Pathway holding the western edge as open ground.",
    windNote:
      "Chinook belt. The river corridor along Carburn Park can carry the wind down its own line, so the park edge is flown with more margin than the interior crescents.",
    seasonalWindow:
      "Green-up reaches the park edge before the lawns, and autumn turns Carburn Park first — the shoulder seasons show the community's park boundary most clearly, and winter opens dark water against snow along the river reach.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone takeoff and landing in municipal parks and along the pathway network, so launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "About 20 minutes from the base — a comfortable same-day booking, and one trip pairs it with Ogden, Douglasdale or Deer Run.",
    faqs: [
      {
        question: "Does drone work in Riverbend need NAV CANADA sign-off?",
        answer:
          "Normally, no. Riverbend lies in uncontrolled Class G airspace outside the charted Calgary control zones, about 12 km from Kirkby Field at Indus, so no airspace authorisation is normally required. The specific site and altitude are still confirmed against NAV Drone before every flight, so the booking carries no paperwork surprises.",
      },
      {
        question: "Can an aerial show how close a Riverbend home is to Carburn Park?",
        answer:
          "Yes — that distance is the frame's whole job. An elevated shot places the address between its street and the Bow River Pathway, with Carburn Park beyond, so a buyer sees in one image what would otherwise take a map and three photographs to explain.",
      },
    ],
    primaryKeywords: [
      "drone photography Riverbend",
      "aerial photography Riverbend",
      "Riverbend drone services",
    ],
    nearestLocations: ["ogden", "douglasdale", "deer-run"],
  },
// ───────────────────────────────────────────────────────────────── SETON ──
  {
    slug: "seton",
    name: "Seton",
    region: "calgary-se",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.8836, lng: -113.9536 },
    tier: 3,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Kirkby Field (Indus)",
      distanceToAerodromeKm: 8,
      authorizationRequired: false,
      authorizationNote:
        "Seton sits in uncontrolled Class G airspace, clear of the charted Calgary-area control zones and about 8 km from Kirkby Field (Indus). Authorisation is not normally required here. Flight plans here also keep clear of South Health Campus heliport, operating about 0 km away. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["South Health Campus heliport"],
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Seton is the dry heart of Calgary's lake district: climb above Seton Boulevard SE and engineered water appears on the horizon in nearly every direction — Auburn Bay to the north, Mahogany to the northeast — while Seton itself keeps to hard surfaces and rooflines.",
      consequence:
        "None of that context survives a ground-level campaign, and a careless aerial one over-claims it — implying lakefront a Seton address does not have.",
      resolution:
        "An orbit planned to place the neighbouring water truthfully on the horizon — behind South Health Campus or the Township Shopping frontage — gives a listing its setting without overselling it, delivered in the standard turnaround.",
    },
    landmarks: [
      "South Health Campus",
      "Seton YMCA",
      "Cineplex Seton",
      "Township Shopping",
      "Seton Boulevard SE",
    ],
    terrain:
      "A flat prairie table at Calgary's southern edge, built out at urban scale — wide arterial blocks around South Health Campus and the Township Shopping frontage, with new residential streets filling in behind them.",
    windNote:
      "Chinook belt. The district's wide, flat blocks offer no terrain shelter, so a west wind crosses Seton at full strength; in winter the gust spread, not the average speed, decides whether a flight goes ahead.",
    seasonalWindow:
      "Flat and open, so snow leaves the district quickly after a chinook and low winter sun throws long, clean shadows across the retail blocks. Summer evenings give the best light on glass and rooflines; late-summer wildfire smoke is the main contrast risk.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone takeoff and landing in municipal parks and on pathways, so Seton launches are staged from private property with the client's consent.",
    subjectMix: [
      "commercial-retail",
      "construction-progress",
      "new-build-subdivision",
      "municipal-infrastructure",
    ],
    mobilisationNote:
      "About 25 minutes from the Calgary base. Same-day scheduling is realistic in routine airspace, and a Seton visit pairs naturally with Auburn Bay, Mahogany or Walden on a single mobilisation.",
    faqs: [
      {
        question: "Does the South Health Campus heliport restrict drone flights in Seton?",
        answer:
          "It shapes them without stopping them. Seton sits in uncontrolled Class G airspace, about 8 km from Kirkby Field at Indus, so no authorisation is normally required — but the hospital heliport operates inside the community, so every flight plan keeps clear of its approaches and is confirmed against NAV Drone first.",
      },
      {
        question: "Can an aerial shoot make Seton read as a lake community?",
        answer:
          "Only honestly. Seton has no lake of its own, but Auburn Bay and Mahogany sit close enough that their water lands on the horizon of a well-planned frame. The orbit is set so the water reads as context, not frontage — the listing gains its setting without a claim it cannot keep.",
      },
      {
        question: "How practical is Seton for repeat construction progress flights?",
        answer:
          "Very. The drive from the Calgary base runs about 25 minutes, the airspace is routine, and the same flight plan can be reflown on each visit so a progress series stays comparable frame to frame. A Seton visit also pairs with Auburn Bay, Mahogany or Walden on one mobilisation.",
      },
    ],
    primaryKeywords: [
      "drone photography Seton",
      "aerial photography Seton",
      "Seton drone services",
    ],
    nearestLocations: ["auburn-bay", "mahogany", "walden"],
  },

  // ────────────────────────────────────────────────────────────── SUNDANCE ──
  {
    slug: "sundance",
    name: "Sundance",
    region: "calgary-se",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.9139, lng: -114.0714 },
    tier: 2,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Kirkby Field (Indus)",
      distanceToAerodromeKm: 15,
      authorizationRequired: false,
      authorizationNote:
        "Sundance sits in uncontrolled Class G airspace, clear of the charted Calgary-area control zones and about 15 km from Kirkby Field (Indus). Authorisation is not normally required here. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Sundance's street network reads from altitude as one closed figure: Sundance Road SE rings the community, Sunmills Drive SE and Sunlake Circle SE coil inward toward Lake Sundance, and every line stops dead where Fish Creek Provincial Park begins.",
      consequence:
        "A ground photograph cannot show where an address sits inside that figure — how near the water lies, how close the park edge runs — and that position is the entire premium of the community.",
      resolution:
        "A boundary-line orbit that holds Lake Sundance and the Fish Creek treeline in the same frame puts the address back in its geography, delivered in the standard turnaround.",
    },
    landmarks: [
      "Lake Sundance",
      "Fish Creek Provincial Park",
      "Sundance Athletic Park",
      "Sundance Road SE",
      "Sunlake Circle SE",
    ],
    terrain:
      "A mature lake suburb on the table above Fish Creek — flat, curving streets wrapped around the engineered water of Lake Sundance, with the land dropping into the wooded creek valley along the community's southern boundary.",
    windNote:
      "Chinook-belt west wind. The Fish Creek valley along the community's south edge gives the wind a channel to run in, so conditions at the park boundary can read stronger than they do deep in the loops.",
    seasonalWindow:
      "The community's aerial character follows the lake and the canopy: ice-off in spring opens the water window, the trees are in full leaf by early summer, and west light late in the day puts reflections on Lake Sundance while the Fish Creek valley falls into shadow first.",
    bylawNote:
      "Calgary's parks bylaws keep drone takeoff and landing out of municipal parks and pathways, and Fish Creek Provincial Park carries its own provincial restrictions — launches are staged from private property with the owner's consent.",
    subjectMix: ["luxury-residential", "municipal-infrastructure"],
    mobilisationNote:
      "About 25 minutes from the Calgary base — a comfortable same-day booking. Chaparral, Walden or Deer Run can be added to the same trip when the light allows.",
    faqs: [
      {
        question: "Is airspace authorisation needed to fly over Lake Sundance?",
        answer:
          "Normally, no. Sundance sits in uncontrolled Class G airspace roughly 15 km from Kirkby Field at Indus, outside the charted Calgary control zones, so routine flights proceed without authorisation. The specific launch point and altitude are still confirmed against NAV Drone before every booking is flown.",
      },
      {
        question: "Can you fly inside Fish Creek Provincial Park for a Sundance shoot?",
        answer:
          "No — the park is provincial land, and drone takeoff and landing inside it are not available for this work. Park-edge frames are captured instead from outside the boundary, launched from private property in Sundance, which is enough to place the wooded valley behind a listing.",
      },
      {
        question: "When does Lake Sundance photograph best from the air?",
        answer:
          "Between spring ice-off and freeze-up, when the water holds colour and reflections instead of reading as a flat white field. Within that window, calm evenings are the prize — the lake mirrors the light, the mature canopy is in leaf, and the community shows the setting buyers pay for.",
      },
    ],
    primaryKeywords: [
      "drone photography Sundance",
      "aerial photography Sundance",
      "Sundance drone services",
    ],
    nearestLocations: ["chaparral", "walden", "deer-run", "deer-ridge"],
  },

  // ──────────────────────────────────────────────────────────────── WALDEN ──
  {
    slug: "walden",
    name: "Walden",
    region: "calgary-se",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.8783, lng: -114.0025 },
    tier: 3,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Kirkby Field (Indus)",
      distanceToAerodromeKm: 11,
      authorizationRequired: false,
      authorizationNote:
        "In uncontrolled Class G airspace about 11 km from Kirkby Field (Indus), Walden lies outside the charted Calgary-area control zones, and no authorisation is normally required. Flight plans here also keep clear of South Health Campus heliport, operating about 4 km away. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["South Health Campus heliport"],
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "On the airspace chart, Walden is defined by absences: it sits in uncontrolled Class G airspace outside every charted Calgary control zone, about 11 km from the Kirkby Field circuit at Indus, with the South Health Campus heliport about 4 km off as the only sensitive traffic nearby.",
      consequence:
        "Clients who assume the whole city carries CYYC authorisation paperwork pad their timelines for approvals Walden does not require — or pass on aerial coverage altogether.",
      resolution:
        "Here the pre-flight work is a NAV Drone confirmation and a plan that keeps clear of the heliport approaches; the booking runs like any routine job and delivers in the standard turnaround.",
    },
    landmarks: [
      "Township Shopping",
      "Pine Creek",
      "Cranston Ridge",
      "Walden Drive SE",
      "Walden Gate SE",
    ],
    terrain:
      "Prairie table at the city's southern limit, still partly under construction — flat new blocks around the Township Shopping district, with the Pine Creek ravine tracing the community's southern flank and Cranston Ridge on the eastern horizon.",
    windNote:
      "Chinook-belt exposure at the city's open southern limit. Immature street trees give the new blocks no shelter, so surface readings are honest here — what the forecast says is generally what the airframe gets.",
    seasonalWindow:
      "A young streetscape with little canopy, so the light works on form rather than foliage — snow clears fast off the open blocks after a chinook, spring green-up arrives street by street as landscaping matures, and long summer evenings suit both rooflines and active construction sites.",
    bylawNote:
      "City of Calgary bylaws restrict launching and landing in municipal parks and along pathways, so flights here stage from private property — a client's lot or a builder's site — with permission in hand.",
    subjectMix: ["new-build-subdivision", "construction-progress", "commercial-retail"],
    mobilisationNote:
      "About 25 minutes from the Calgary base. Same-day is realistic, and Legacy, Chaparral and Cranston are close enough to share the mobilisation on a multi-property day.",
    faqs: [
      {
        question: "Do drone shoots in Walden require NAV CANADA authorisation?",
        answer:
          "Normally, no. Walden sits in uncontrolled Class G airspace about 11 km from Kirkby Field at Indus, outside the charted Calgary control zones. The South Health Campus heliport operates about 4 km away, so flight plans keep clear of its approaches, and each site is confirmed against NAV Drone before flying.",
      },
      {
        question: "How quickly can a Walden shoot be scheduled?",
        answer:
          "The drive from the Calgary base is about 25 minutes and the airspace is routine, so a Walden booking is limited by weather rather than paperwork — same-day is often realistic. Builders running work in Legacy or Cranston can fold a Walden visit into the same mobilisation.",
      },
    ],
    primaryKeywords: [
      "drone photography Walden",
      "aerial photography Walden",
      "Walden drone services",
    ],
    nearestLocations: ["legacy", "chaparral", "cranston"],
  },

  // ─────────────────────────────────────────────────────────── WILLOW PARK ──
  {
    slug: "willow-park",
    name: "Willow Park",
    region: "calgary-se",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.9678, lng: -114.0522 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Kirkby Field (Indus)",
      distanceToAerodromeKm: 14,
      authorizationRequired: false,
      authorizationNote:
        "Willow Park sits in uncontrolled Class G airspace, clear of the charted Calgary-area control zones and about 14 km from Kirkby Field (Indus). Authorisation is not normally required here. Flight plans here also keep clear of Rockyview General Hospital heliport, operating about 4 km away. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Rockyview General Hospital heliport"],
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "The streets of Willow Park were drawn around the golf course, not the other way round — from the air, Willingdon Boulevard SE and Woburn Road SE trace the fairway edges of Willow Park Golf Course, and the community reads as a course with a neighbourhood threaded through it.",
      consequence:
        "The premium lots back onto those fairways, and from the street they look like every other frontage on the block — the thing the buyer is paying for sits entirely behind the house.",
      resolution:
        "A rising reveal from the back of the lot lifts the fairway and the mature canopy into the frame behind the roofline, delivered in the standard turnaround.",
    },
    landmarks: [
      "Willow Park Golf Course",
      "Southcentre Mall",
      "Chinook Park",
      "Willingdon Boulevard SE",
      "Woburn Road SE",
    ],
    terrain:
      "Flat, fully treed ground where decades of canopy and the fairways of Willow Park Golf Course supply the relief — from altitude the course reads as a green interior the community was drawn around.",
    windNote:
      "Chinook-belt west wind, taken with a caveat: a mature canopy filters what you feel at ground level, so a street that seems sheltered can still be gusting at flight altitude. The forecast, not the front yard, sets the call.",
    seasonalWindow:
      "This is a canopy community, and its aerial identity follows the leaves — from green-up through early fall the streets read as forest with rooflines in it, while the golf course holds its colour longest. Bare-tree winter frames trade softness for structure, showing lot lines the summer canopy hides.",
    bylawNote:
      "Calgary's parks bylaws do not allow drones to take off or land in city parks or on pathways, so Willow Park flights launch from private property — a client's yard or parking area — with the owner's consent.",
    subjectMix: ["luxury-residential", "commercial-retail"],
    mobilisationNote:
      "About 20 minutes from the Calgary base — among the shortest runs in the southeast rotation. Same-day is realistic, and Acadia, Fairview or Lake Bonavista sit minutes away for a paired booking.",
    faqs: [
      {
        question: "Does the Rockyview hospital heliport affect drone photography in Willow Park?",
        answer:
          "It is planned around, not a barrier. Willow Park lies in uncontrolled Class G airspace about 14 km from Kirkby Field at Indus, so no authorisation is normally required. The Rockyview General heliport sits about 4 km away; flight plans keep clear of its approaches and every site is checked on NAV Drone.",
      },
      {
        question: "Can you photograph a home that backs onto Willow Park Golf Course?",
        answer:
          "Yes. The flight launches from the client's own property and orbits the lot from legal altitude, putting the fairway and mature canopy behind the roofline — the view the back fence hides from the street. Taking off from course land itself would need the course's permission, so plans avoid it.",
      },
      {
        question: "Is aerial coverage useful for commercial space near Southcentre Mall?",
        answer:
          "Yes — retail leasing here sells on position, and position is an aerial fact. A frame from altitude shows a unit's relationship to Southcentre Mall, the surrounding parking and the arterial traffic that feeds it, which no ground photograph can establish. Routine airspace keeps the booking simple.",
      },
    ],
    primaryKeywords: [
      "drone photography Willow Park",
      "aerial photography Willow Park",
      "Willow Park drone services",
    ],
    nearestLocations: ["chaparral", "acadia", "lake-bonavista", "fairview"],
  }
];
